<template>
  <div class="color-picker-comp">
    <div class="picker-row">
      <el-color-picker v-model="color" size="medium" show-alpha />
      <span class="color-hex">{{ color || $t('tool.ColorPicker.pickColor') }}</span>
    </div>

    <div class="color-values" v-if="color && rgb">
      <div class="cv-swatch" :style="{ background: color }" />
      <div class="cv-list">
        <div class="cv-item">
          <span class="cv-label">HEX</span>
          <code class="cv-value">{{ hex }}</code>
          <el-button type="text" size="mini" @click="copy(hex)">{{ $t("common.action.copy") }}</el-button>
        </div>
        <div class="cv-item">
          <span class="cv-label">RGB</span>
          <code class="cv-value">{{ rgb }}</code>
          <el-button type="text" size="mini" @click="copy(rgb)">{{ $t("common.action.copy") }}</el-button>
        </div>
        <div class="cv-item" v-if="hsl">
          <span class="cv-label">HSL</span>
          <code class="cv-value">{{ hsl }}</code>
          <el-button type="text" size="mini" @click="copy(hsl)">{{ $t("common.action.copy") }}</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorPickerTool',
  props: { config: { type: Object, default: () => ({}) } },
  data: () => ({ color: null }),
  computed: {
    parsed() {
      if (!this.color) return null
      let r, g, b, a = 1
      const c = this.color.trim()

      // hex: #RGB, #RRGGBB, #RRGGBBAA
      if (c.startsWith('#')) {
        let h = c.slice(1)
        if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2]
        if (h.length >= 6) {
          r = parseInt(h.substring(0,2), 16)
          g = parseInt(h.substring(2,4), 16)
          b = parseInt(h.substring(4,6), 16)
        }
        if (h.length === 8) a = parseInt(h.substring(6,8), 16) / 255
      }

      // rgba(r,g,b,a) or rgb(r,g,b)
      const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
      if (m) {
        r = parseInt(m[1])
        g = parseInt(m[2])
        b = parseInt(m[3])
        a = m[4] !== undefined ? parseFloat(m[4]) : 1
      }

      if (r === undefined) return null
      return { r, g, b, a }
    },
    hex() {
      if (!this.parsed) return ''
      const { r, g, b, a } = this.parsed
      const toHex = n => n.toString(16).padStart(2, '0').toUpperCase()
      let h = `#${toHex(r)}${toHex(g)}${toHex(b)}`
      if (a < 1) h += toHex(Math.round(a * 255))
      return h
    },
    rgb() {
      if (!this.parsed) return ''
      const { r, g, b, a } = this.parsed
      return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})` : `rgb(${r}, ${g}, ${b})`
    },
    hsl() {
      if (!this.parsed) return ''
      let { r, g, b } = this.parsed
      r /= 255; g /= 255; b /= 255
      const max = Math.max(r, g, b), min = Math.min(r, g, b)
      let h = 0, s, l = (max + min) / 2
      if (max === min) { h = s = 0 }
      else {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
          case g: h = ((b - r) / d + 2) / 6; break
          case b: h = ((r - g) / d + 4) / 6; break
        }
      }
      return `hsl(${Math.round(h*360)}, ${Math.round(s*100)}%, ${Math.round(l*100)}%)`
    }
  },
  methods: {
    copy(val) { navigator.clipboard.writeText(val); this.$message.success(this.$t("common.status.copied")) },
    doAction(action) { if (action === 'copy') this.copy(this.hex) }
  }
}
</script>

<style scoped>
.color-picker-comp { }

.picker-row { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; }
.color-hex { font-size: 16px; color: #1a1f36; font-family: monospace; }

.color-values { display: flex; gap: 20px; align-items: flex-start; }
.cv-swatch {
  width: 80px; height: 80px; border-radius: 12px; flex-shrink: 0;
  border: 2px solid #e4e7ed; box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.cv-list { flex: 1; }
.cv-item { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.cv-label { font-size: 13px; font-weight: 600; color: #8892a6; width: 36px; flex-shrink: 0; }
.cv-value { font-size: 14px; font-family: monospace; color: #303133; background: #f5f6f8; padding: 2px 10px; border-radius: 4px; }
</style>
